import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type {
  BreathworkStyle,
  BreathworkForm,
  BreathworkSession,
  BreathworkStyleProgress,
  BreathworkStreak,
  BreathworkAchievement,
  BreathingSessionState
} from '../types';
import {
  BREATHWORK_STYLES,
  BREATHWORK_FORMS,
  calculateRankFromXP,
  getRankTitle,
  getProgressToNextRank,
  getXPForNextRank
} from '../constants/breathwork';
import { useActivityLogStore } from './activityLogStore';
import { POINTS } from '../utils/levelCalculation';
import { supabase } from '../lib/supabase';
import { syncManager } from '../lib/syncManager';
import { dedupeInit, isCloudFresh, markCloudFresh } from '../lib/cloudBoot';
import {
  accountGeneration, accountStorage, cachedUserIdOwner, scopeToAccount,
} from '../lib/accountStorage';
import { changedFields, createDocSync, type DocBase, type DocRules, type DocSync } from '../lib/syncDoc';
import { supabaseBlobTransport } from '../lib/syncBlobTransport';
import { loadedFor, refreshBlobOnFocus } from '../lib/blobFocusRefresh';

// ─── Supabase sync (sync epic) ──────────────────────────────────────────────
// Breathwork user-data syncs as ONE row per user (user_breathwork.data):
// sessions / styleProgress / streak / achievements / activeForms / preferences.
// Styles + forms are reference data; the in-flight session and level-up info are
// transient — none are synced.
//
// ⚠️⚠️ Writes go through syncDoc (lib/syncDoc.ts), NOT a whole-row upsert. The
// old upsert wrote this tab's copy of everything, so a session completed on
// another device — and the XP it earned — vanished the next time a stale tab
// saved anything. Now a write re-reads the row and applies only what THIS tab
// changed: sessions by id, and a style's counters by the increments this tab
// made, so sessions on two devices both count.

const USER_BREATHWORK_TABLE = 'user_breathwork';

function pickBreathwork(s: Pick<BreathworkState, keyof BreathworkDoc>): BreathworkDoc {
  return {
    sessions: s.sessions,
    styleProgress: s.styleProgress,
    streak: s.streak,
    achievements: s.achievements,
    activeForms: s.activeForms,
    acknowledgedRiskStyles: s.acknowledgedRiskStyles,
    preferences: s.preferences,
  };
}

interface BreathworkDoc {
  sessions: BreathworkSession[];
  styleProgress: Record<string, BreathworkStyleProgress>;
  streak: BreathworkStreak | null;
  achievements: BreathworkAchievement[];
  activeForms: Record<string, string>;
  acknowledgedRiskStyles: string[];
  preferences: BreathworkState['preferences'];
}

const DEFAULT_PREFERENCES: BreathworkState['preferences'] = {
  audioEnabled: true,
  hapticsEnabled: true,
  visualIntensity: 'high',
};

const isObject = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);

/** A cloud blob (or none) as the full document. */
function hydrateBreathwork(data: unknown): BreathworkDoc {
  const d = isObject(data) ? data : {};
  return {
    sessions: Array.isArray(d.sessions) ? (d.sessions as BreathworkSession[]) : [],
    styleProgress: isObject(d.styleProgress) ? (d.styleProgress as Record<string, BreathworkStyleProgress>) : {},
    streak: (d.streak as BreathworkStreak | null | undefined) ?? null,
    achievements: Array.isArray(d.achievements) ? (d.achievements as BreathworkAchievement[]) : [],
    activeForms: isObject(d.activeForms) ? (d.activeForms as Record<string, string>) : {},
    acknowledgedRiskStyles: Array.isArray(d.acknowledgedRiskStyles) ? (d.acknowledgedRiskStyles as string[]) : [],
    preferences: isObject(d.preferences) ? (d.preferences as BreathworkState['preferences']) : DEFAULT_PREFERENCES,
  };
}

/**
 * Both sides practised a style since this tab's base: the cloud's counters plus
 * the increments made HERE — so neither device's sessions are lost.
 */
function mergeProgress(base: unknown, local: unknown, cloud: unknown): BreathworkStyleProgress | undefined {
  const b = base as BreathworkStyleProgress | undefined;
  const l = local as BreathworkStyleProgress | undefined;
  const c = cloud as BreathworkStyleProgress | undefined;
  if (!l || !c) return l ?? c;
  const add = (k: 'totalXp' | 'styleMasteryPoints' | 'totalSessions' | 'totalMinutes') =>
    (c[k] ?? 0) + (l[k] ?? 0) - (b?.[k] ?? 0);
  const styleMasteryPoints = add('styleMasteryPoints');
  return {
    ...c,
    totalXp: add('totalXp'),
    styleMasteryPoints,
    currentLevel: calculateRankFromXP(styleMasteryPoints),
    totalSessions: add('totalSessions'),
    totalMinutes: add('totalMinutes'),
    unlockedForms: [...new Set([...(c.unlockedForms ?? []), ...(l.unlockedForms ?? [])])],
    updatedAt: l.updatedAt > c.updatedAt ? l.updatedAt : c.updatedAt,
  };
}

/** Both sides moved the streak: the later practice day wins; the longest streak is the longest of all. */
function mergeStreak(_base: unknown, local: unknown, cloud: unknown): BreathworkStreak | null {
  const l = local as BreathworkStreak | null;
  const c = cloud as BreathworkStreak | null;
  if (!l || !c) return l ?? c;
  const ld = l.lastPracticeDate ?? '';
  const cd = c.lastPracticeDate ?? '';
  const newer = ld > cd || (ld === cd && l.currentStreak > c.currentStreak) ? l : c;
  return { ...newer, longestStreak: Math.max(l.longestStreak, c.longestStreak, newer.currentStreak) };
}

const BREATHWORK_RULES: DocRules<BreathworkDoc> = {
  sessions: { records: 'id' },
  styleProgress: { map: mergeProgress },
  streak: mergeStreak,
  // One per achievement: two devices earning "First Breath" keep one.
  achievements: { records: 'achievementId' },
  activeForms: { map: 'value' },
  acknowledgedRiskStyles: 'set',
  preferences: { map: 'value' },
};

const breathworkSync: DocSync<BreathworkDoc> = createDocSync<BreathworkDoc>({
  transport: supabaseBlobTransport(USER_BREATHWORK_TABLE),
  owner: () => useBreathworkStore.getState().cachedUserId,
  rules: BREATHWORK_RULES,
  hydrate: hydrateBreathwork,
  read: () => pickBreathwork(useBreathworkStore.getState()),
  readBase: () => useBreathworkStore.getState().syncBase,
  adopt: (doc, base) => useBreathworkStore.setState((state: BreathworkState) => ({
    ...changedFields(pickBreathwork(state), doc),
    syncBase: base,
  })),
  onError: (error) => console.error('Failed to sync breathwork to Supabase:', error),
});

/** Await the pending breathwork write (tests). */
export function flushBreathworkSync(): Promise<void> {
  return breathworkSync.flush();
}

// A tab that comes back shows sessions and XP from elsewhere (lib/blobFocusRefresh.ts).
refreshBlobOnFocus(USER_BREATHWORK_TABLE, () => breathworkSync.refresh(loadedFor(() => useBreathworkStore.getState().initializedUserId)));

interface BreathworkState {
  styles: BreathworkStyle[];
  forms: BreathworkForm[];
  sessions: BreathworkSession[];
  styleProgress: Record<string, BreathworkStyleProgress>;
  streak: BreathworkStreak | null;
  achievements: BreathworkAchievement[];
  activeForms: Record<string, string>;
  /** Style ids for which the user has acknowledged the high-risk safety screen (BW1). */
  acknowledgedRiskStyles: string[];

  currentSession: {
    form: BreathworkForm | null;
    style: BreathworkStyle | null;
    state: BreathingSessionState | null;
    startTime: number | null;
    beforeState: { energy: number; calm: number; focus: number } | null;
  };

  lastCompletedSession: {
    styleId: string;
    styleIcon: string;
    formName: string;
    styleName: string;
    styleColor: string;
    expectedEffect: string;
    durationSeconds: number;
    cyclesCompleted: number;
    xpEarned: number;
    beforeState: { energy: number; calm: number; focus: number };
    afterState: { energy: number; calm: number; focus: number };
  } | null;

  levelUpInfo: {
    styleId: string;
    styleName: string;
    styleIcon: string;
    styleColor: string;
    oldLevel: number;
    newLevel: number;
    oldTitle: string;
    newTitle: string;
    newSMP: number;
  } | null;

  preferences: {
    audioEnabled: boolean;
    hapticsEnabled: boolean;
    visualIntensity: 'low' | 'medium' | 'high';
  };

  userLevel: number;
  initializedUserId: string | null;
  cachedUserId: string | null;
  /** The last cloud copy this tab agreed with (sessions hashed per record) — see lib/syncDoc.ts. */
  syncBase: DocBase<BreathworkDoc> | null;

  initialize: () => Promise<void>;
  initializeStyles: () => void;
  getStyleById: (id: string) => BreathworkStyle | undefined;
  getFormsByStyle: (styleId: string) => BreathworkForm[];
  getFormById: (formId: string) => BreathworkForm | undefined;

  startSession: (formId: string, beforeState: { energy: number; calm: number; focus: number }) => void;
  updateSessionState: (state: BreathingSessionState) => void;
  completeSession: (afterState: { energy: number; calm: number; focus: number }) => Promise<void>;
  cancelSession: () => void;

  getStyleProgress: (styleId: string) => BreathworkStyleProgress & { levelTitle: string; progressToNext: { current: number; needed: number; percent: number } };
  getTodayStats: () => { sessions: number; minutes: number; xp: number };
  getWeekStats: () => { sessions: number; minutes: number; xp: number };

  calculateXP: (durationSeconds: number) => number;
  checkAchievements: () => void;

  setPreference: (key: keyof BreathworkState['preferences'], value: any) => void;
  setUserLevel: (level: number) => void;
  clearLevelUpInfo: () => void;
  setActiveForm: (styleId: string, formId: string) => void;
  getActiveFormForStyle: (styleId: string) => BreathworkForm | undefined;
  acknowledgeRiskStyle: (styleId: string) => void;
  hasAcknowledgedRiskStyle: (styleId: string) => boolean;
}

export const useBreathworkStore = create<BreathworkState>()(
  persist<BreathworkState, [], [], Partial<BreathworkState>>(
    (rawSet, get) => {
      // ⚠️ The first edit of a tab whose load never finished records its base
      // (see the same wrapper in userStore).
      const set: typeof rawSet = (...args: Parameters<typeof rawSet>) => {
        if (get().syncBase === null) rawSet({ syncBase: breathworkSync.baseOf(pickBreathwork(get())) });
        return rawSet(...args);
      };
      const sync = () => breathworkSync.schedule();
      return {
      styles: BREATHWORK_STYLES,
      forms: BREATHWORK_FORMS,
      sessions: [],
      styleProgress: {},
      streak: null,
      achievements: [],
      activeForms: {},
      acknowledgedRiskStyles: [],

      currentSession: {
        form: null,
        style: null,
        state: null,
        startTime: null,
        beforeState: null
      },

      lastCompletedSession: null,

      levelUpInfo: null,

      preferences: { ...DEFAULT_PREFERENCES },

      userLevel: 1,
      initializedUserId: null,
      cachedUserId: null,
      syncBase: null,

      // Load-on-login from the user_breathwork blob, keyed by user id (re-runs
      // on account switch). Runs on the write chain, so no write interleaves.
      initialize: () => dedupeInit('user_breathwork', () => breathworkSync.exclusive(async () => {
        const at = accountGeneration();
        try {
          const { user } = await syncManager.safeAuthCheck();
          if (!user) return;
          if (get().initializedUserId === user.id) return;
          // Cache-first boot: while this user's local cache is fresh, skip the
          // cloud read entirely (write-through keeps cloud current meanwhile).
          if (get().cachedUserId === user.id && isCloudFresh('user_breathwork', user.id)) {
            rawSet((state) => ({
              initializedUserId: user.id,
              syncBase: state.syncBase ?? breathworkSync.baseOf(pickBreathwork(state)),
            }));
            return;
          }

          const { data, error } = await supabase
            .from(USER_BREATHWORK_TABLE)
            .select('*');
          // An answer for an account that has since signed out is not this copy's.
          if (at !== accountGeneration()) return;
          if (error) throw error;

          const row = (data ?? [])[0];
          const cloudData = row && row.data && typeof row.data === 'object' && Object.keys(row.data).length > 0
            ? row.data
            : null;
          // The cloud wins for everything this tab has not changed since its
          // base; a cache that is another account's is replaced outright.
          const state = get();
          const cacheIsOurs = state.cachedUserId === null || state.cachedUserId === user.id;
          let unsaved = false;
          if (cacheIsOurs) unsaved = breathworkSync.reconcile(cloudData);
          else breathworkSync.replace(cloudData);
          breathworkSync.noteLoaded(row?.updated_at ?? null);
          rawSet({ initializedUserId: user.id, cachedUserId: user.id });
          markCloudFresh('user_breathwork', user.id);
          if (unsaved) sync();
        } catch (error) {
          console.error('Failed to initialize breathwork from Supabase:', error);
        }
      })),

      initializeStyles: () => {
        set({ styles: BREATHWORK_STYLES, forms: BREATHWORK_FORMS });
      },

      getStyleById: (id: string) => {
        return get().styles.find(s => s.id === id);
      },

      getFormsByStyle: (styleId: string) => {
        return get().forms.filter(f => f.styleId === styleId);
      },

      getFormById: (formId: string) => {
        return get().forms.find(f => f.id === formId);
      },

      startSession: (formId: string, beforeState: { energy: number; calm: number; focus: number }) => {
        const form = get().getFormById(formId);
        if (!form) return;

        const style = get().getStyleById(form.styleId);
        if (!style) return;

        set({
          currentSession: {
            form,
            style,
            state: {
              phase: 'inhale',
              currentCycle: 1,
              currentRound: 1,
              elapsedSeconds: 0,
              phaseProgress: 0
            },
            startTime: Date.now(),
            beforeState
          }
        });
      },

      updateSessionState: (state: BreathingSessionState) => {
        set(prevState => ({
          currentSession: {
            ...prevState.currentSession,
            state
          }
        }));
      },

      completeSession: async (afterState: { energy: number; calm: number; focus: number }) => {
        const { currentSession, userLevel } = get();
        if (!currentSession.form || !currentSession.style || !currentSession.startTime || !currentSession.state) {
          return;
        }

        const durationSeconds = Math.floor((Date.now() - currentSession.startTime) / 1000);
        const xpEarned = get().calculateXP(durationSeconds);

        const completedSessionData = {
          styleId: currentSession.style.id,
          styleIcon: currentSession.style.icon,
          formName: currentSession.form.name,
          styleName: currentSession.style.name,
          styleColor: currentSession.style.color,
          expectedEffect: currentSession.form.expectedEffect,
          durationSeconds,
          cyclesCompleted: currentSession.state.currentCycle,
          xpEarned,
          beforeState: currentSession.beforeState || { energy: 50, calm: 50, focus: 50 },
          afterState
        };

        const session: BreathworkSession = {
          id: crypto.randomUUID(),
          userId: 'local-user',
          styleId: currentSession.style.id,
          formId: currentSession.form.id,
          formName: currentSession.form.name,
          durationSeconds,
          cyclesCompleted: currentSession.state.currentCycle,
          xpEarned,
          userLevelAtCompletion: userLevel,
          adherencePercentage: 100,
          stateBeforeEnergy: currentSession.beforeState?.energy || 50,
          stateBeforeCalm: currentSession.beforeState?.calm || 50,
          stateBeforeFocus: currentSession.beforeState?.focus || 50,
          stateAfterEnergy: afterState.energy,
          stateAfterCalm: afterState.calm,
          stateAfterFocus: afterState.focus,
          completedAt: new Date().toISOString(),
          createdAt: new Date().toISOString()
        };

        set(state => {
          const newSessions = [...state.sessions, session];

          const existingProgress = state.styleProgress[session.styleId] || {
            id: crypto.randomUUID(),
            userId: 'local-user',
            styleId: session.styleId,
            totalXp: 0,
            styleMasteryPoints: 0,
            currentLevel: 0,
            totalSessions: 0,
            totalMinutes: 0,
            masteryLevel: 1,
            unlockedForms: ['beginner'],
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          };

          const oldLevel = existingProgress.currentLevel;
          const oldSMP = existingProgress.styleMasteryPoints;
          const newSMP = oldSMP + durationSeconds;
          const newLevel = calculateRankFromXP(newSMP);

          const updatedProgress: BreathworkStyleProgress = {
            ...existingProgress,
            totalXp: existingProgress.totalXp + xpEarned,
            styleMasteryPoints: newSMP,
            currentLevel: newLevel,
            totalSessions: existingProgress.totalSessions + 1,
            totalMinutes: existingProgress.totalMinutes + Math.floor(durationSeconds / 60),
            updatedAt: new Date().toISOString()
          };

          let levelUpData = null;
          if (newLevel > oldLevel) {
            const style = state.styles.find(s => s.id === session.styleId);
            if (style) {
              levelUpData = {
                styleId: session.styleId,
                styleName: style.name,
                styleIcon: style.icon,
                styleColor: style.color,
                oldLevel,
                newLevel,
                oldTitle: getRankTitle(session.styleId, oldLevel, style.name, style.icon),
                newTitle: getRankTitle(session.styleId, newLevel, style.name, style.icon),
                newSMP
              };
            }
          }

          const today = new Date().toISOString().split('T')[0];
          const lastPracticeDate = state.streak?.lastPracticeDate;
          const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];

          let newStreak: BreathworkStreak;
          if (!state.streak) {
            newStreak = {
              id: crypto.randomUUID(),
              userId: 'local-user',
              currentStreak: 1,
              longestStreak: 1,
              lastPracticeDate: today,
              streakFreezeCount: 0,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString()
            };
          } else if (lastPracticeDate === today) {
            newStreak = state.streak;
          } else if (lastPracticeDate === yesterday) {
            const newCurrentStreak = state.streak.currentStreak + 1;
            newStreak = {
              ...state.streak,
              currentStreak: newCurrentStreak,
              longestStreak: Math.max(newCurrentStreak, state.streak.longestStreak),
              lastPracticeDate: today,
              updatedAt: new Date().toISOString()
            };
          } else {
            newStreak = {
              ...state.streak,
              currentStreak: 1,
              lastPracticeDate: today,
              updatedAt: new Date().toISOString()
            };
          }

          return {
            sessions: newSessions,
            styleProgress: {
              ...state.styleProgress,
              [session.styleId]: updatedProgress
            },
            streak: newStreak,
            levelUpInfo: levelUpData,
            lastCompletedSession: completedSessionData,
            currentSession: {
              form: null,
              style: null,
              state: null,
              startTime: null,
              beforeState: null
            }
          };
        });

        // Log breathwork activity
        const style = get().styles.find(s => s.id === session.styleId);
        if (style) {
          useActivityLogStore.getState().addActivity({
            activityType: 'breathwork',
            activityName: `${style.name} - ${session.formName}`,
            domainId: 'personal',
            xpEarned: session.xpEarned,
            metadata: {
              styleId: session.styleId,
              styleName: style.name,
              formName: session.formName,
              durationSeconds: session.durationSeconds,
              cyclesCompleted: session.cyclesCompleted
            },
            completedAt: session.completedAt
          });
        }

        get().checkAchievements();

        // Write-through: a completed session is the main breathwork XP event.
        sync();
      },

      cancelSession: () => {
        set({
          currentSession: {
            form: null,
            style: null,
            state: null,
            startTime: null,
            beforeState: null
          }
        });
      },

      getStyleProgress: (styleId: string) => {
        const progress = get().styleProgress[styleId];
        const style = get().styles.find(s => s.id === styleId);

        const baseProgress: BreathworkStyleProgress = progress || {
          id: crypto.randomUUID(),
          userId: 'local-user',
          styleId,
          totalXp: 0,
          styleMasteryPoints: 0,
          currentLevel: 0,
          totalSessions: 0,
          totalMinutes: 0,
          masteryLevel: 1,
          unlockedForms: ['beginner'],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };

        const smp = baseProgress.styleMasteryPoints || 0;
        const currentLevel = baseProgress.currentLevel || calculateRankFromXP(smp);
        const levelTitle = style ? getRankTitle(styleId, currentLevel, style.name, style.icon) : 'Initiate';
        const progressToNext = getProgressToNextRank(smp, currentLevel);

        return {
          ...baseProgress,
          currentLevel,
          levelTitle,
          progressToNext
        };
      },

      getTodayStats: () => {
        const today = new Date().toISOString().split('T')[0];
        const todaySessions = get().sessions.filter(s =>
          s.completedAt.startsWith(today)
        );

        return {
          sessions: todaySessions.length,
          minutes: todaySessions.reduce((sum, s) => sum + Math.floor(s.durationSeconds / 60), 0),
          xp: todaySessions.reduce((sum, s) => sum + s.xpEarned, 0)
        };
      },

      getWeekStats: () => {
        const weekAgo = new Date(Date.now() - 7 * 86400000).toISOString();
        const weekSessions = get().sessions.filter(s =>
          s.completedAt >= weekAgo
        );

        return {
          sessions: weekSessions.length,
          minutes: weekSessions.reduce((sum, s) => sum + Math.floor(s.durationSeconds / 60), 0),
          xp: weekSessions.reduce((sum, s) => sum + s.xpEarned, 0)
        };
      },

      calculateXP: (durationSeconds: number) => {
        // Config-backed (console → XP economy). Defaults match the historical
        // local constants (1 XP/sec, ×1.2 per user level).
        const baseXP = POINTS.BREATHWORK_PER_SECOND ?? 1;
        const scaling = POINTS.BREATHWORK_SCALING ?? 1.2;
        const userLevel = get().userLevel;

        const scalingFactor = Math.pow(scaling, userLevel - 1);
        return Math.round(durationSeconds * baseXP * scalingFactor);
      },

      checkAchievements: () => {
        const state = get();
        const existingAchievementIds = new Set(state.achievements.map(a => a.achievementId));
        const newAchievements: BreathworkAchievement[] = [];

        if (state.sessions.length === 1 && !existingAchievementIds.has('first-breath')) {
          newAchievements.push({
            id: crypto.randomUUID(),
            userId: 'local-user',
            achievementId: 'first-breath',
            achievementName: 'First Breath',
            achievementType: 'milestone',
            earnedAt: new Date().toISOString(),
            createdAt: new Date().toISOString()
          });
        }

        if (state.sessions.length === 10 && !existingAchievementIds.has('ten-sessions')) {
          newAchievements.push({
            id: crypto.randomUUID(),
            userId: 'local-user',
            achievementId: 'ten-sessions',
            achievementName: 'Breathwork Apprentice',
            achievementType: 'milestone',
            earnedAt: new Date().toISOString(),
            createdAt: new Date().toISOString()
          });
        }

        const totalMinutes = state.sessions.reduce((sum, s) => sum + Math.floor(s.durationSeconds / 60), 0);
        if (totalMinutes >= 100 && !existingAchievementIds.has('hundred-minutes')) {
          newAchievements.push({
            id: crypto.randomUUID(),
            userId: 'local-user',
            achievementId: 'hundred-minutes',
            achievementName: 'Century Mark',
            achievementType: 'milestone',
            earnedAt: new Date().toISOString(),
            createdAt: new Date().toISOString()
          });
        }

        if (state.streak && state.streak.currentStreak >= 7 && !existingAchievementIds.has('seven-day-streak')) {
          newAchievements.push({
            id: crypto.randomUUID(),
            userId: 'local-user',
            achievementId: 'seven-day-streak',
            achievementName: 'Week Warrior',
            achievementType: 'streak',
            earnedAt: new Date().toISOString(),
            createdAt: new Date().toISOString()
          });
        }

        const uniqueStyles = new Set(state.sessions.map(s => s.styleId));
        if (uniqueStyles.size === 7 && !existingAchievementIds.has('all-styles')) {
          newAchievements.push({
            id: crypto.randomUUID(),
            userId: 'local-user',
            achievementId: 'all-styles',
            achievementName: 'Elemental Explorer',
            achievementType: 'exploration',
            earnedAt: new Date().toISOString(),
            createdAt: new Date().toISOString()
          });
        }

        if (newAchievements.length > 0) {
          set(state => ({
            achievements: [...state.achievements, ...newAchievements]
          }));
        }
      },

      setPreference: (key, value) => {
        set(state => ({
          preferences: {
            ...state.preferences,
            [key]: value
          }
        }));
        sync();
      },

      setUserLevel: (level: number) => {
        set({ userLevel: level });
      },

      clearLevelUpInfo: () => {
        set({ levelUpInfo: null });
      },

      setActiveForm: (styleId: string, formId: string) => {
        set(state => ({
          activeForms: {
            ...state.activeForms,
            [styleId]: formId
          }
        }));
        sync();
      },

      getActiveFormForStyle: (styleId: string) => {
        const state = get();
        const activeFormId = state.activeForms[styleId];

        const styleForms = state.forms.filter(f => f.styleId === styleId);

        if (activeFormId) {
          // Fall back to the style's first Form if the saved id is stale (e.g.
          // a pre-v2 form id that no longer exists in the catalog).
          const found = state.forms.find(f => f.id === activeFormId);
          if (found) return found;
        }

        return styleForms.length > 0 ? styleForms[0] : undefined;
      },

      acknowledgeRiskStyle: (styleId: string) => {
        if (get().acknowledgedRiskStyles.includes(styleId)) return;
        set((state) => ({ acknowledgedRiskStyles: [...state.acknowledgedRiskStyles, styleId] }));
        sync();
      },

      hasAcknowledgedRiskStyle: (styleId: string): boolean =>
        get().acknowledgedRiskStyles.includes(styleId),
      };
    },
    {
      name: 'breathwork-storage',
      // ⭐ One saved copy per account — see `scopeToAccount` below.
      storage: accountStorage(cachedUserIdOwner),
      version: 3,
      // v3 (Breathwork v2): the Form catalog grew from 7 → 49 Forms with a new
      // schema. Drop the persisted reference data so the in-code constants are
      // authoritative; initializeStyles() reloads them on mount. User data
      // (sessions/progress/streak/activeForms) is preserved — a stale activeForms
      // id falls back gracefully in getActiveFormForStyle.
      migrate: (persistedState: any, version: number) => {
        if (version < 3 && persistedState) {
          delete persistedState.styles;
          delete persistedState.forms;
        }
        return persistedState as BreathworkState;
      },
      // Persist reference + user data + cache owner; keep the in-flight session,
      // level-up celebration, and initializedUserId transient.
      partialize: (state) => ({
        styles: state.styles,
        forms: state.forms,
        sessions: state.sessions,
        styleProgress: state.styleProgress,
        streak: state.streak,
        achievements: state.achievements,
        activeForms: state.activeForms,
        acknowledgedRiskStyles: state.acknowledgedRiskStyles,
        preferences: state.preferences,
        userLevel: state.userLevel,
        cachedUserId: state.cachedUserId,
        syncBase: state.syncBase,
      }) as any,
    }
  )
);

/* ⚠️⚠️ ONE SAVED COPY PER ACCOUNT (lib/accountStorage). The device kept one copy, tagged with the
   account it was loaded for, and a sign-out does not reload the page: an account's edits whose
   write had not landed lived only until the next account's load replaced the copy, and the next
   account's edits made before its own load were merged into the last one's row by ITS next load. */
scopeToAccount(useBreathworkStore);
